import React, { memo, useEffect, useMemo, useState } from 'react';

import { getEntityValue } from 'lib/services/valueLists';
import { MultiSelectSummaryChip } from './MultiSelectValuesPopover';
import {
    parseMultiSelectIds,
    parseMultiSelectOptions,
    resolveMultiSelectColumnLabel,
    resolveRowFieldValue,
} from './multiSelectCellUtils';

const needsLabelHydration = (options) =>
    options.length > 0 && options.every((option) => option.label === option.value);

const MultiSelectReadCell = memo((params) => {
    const field = params.colDef?.field;
    const rawValue = field ? resolveRowFieldValue(params.data, field) : params.value;
    const fieldLabel = resolveMultiSelectColumnLabel(params);
    const list =
        params.cellRendererParams?.list ||
        params.inputParams?.list ||
        params.colDef?.cellRendererParams?.list;

    const parsedOptions = useMemo(() => parseMultiSelectOptions(rawValue), [rawValue]);
    const [hydratedLabels, setHydratedLabels] = useState(null);

    useEffect(() => {
        if (!needsLabelHydration(parsedOptions) || !list) {
            setHydratedLabels(null);
            return undefined;
        }

        const ids = parseMultiSelectIds(rawValue);
        if (!ids.length) {
            setHydratedLabels(null);
            return undefined;
        }

        let cancelled = false;
        getEntityValue(list, ids).then((result) => {
            if (cancelled || !result?.data) return;

            const labels = ids
                .map((id) => {
                    const row = result.data[id] ?? result.data[String(id)];
                    return row?.description ?? row?.label ?? String(id);
                })
                .filter(Boolean);

            if (labels.length) {
                setHydratedLabels(labels);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [list, parsedOptions, rawValue]);

    const labels = useMemo(() => {
        if (hydratedLabels?.length) {
            return hydratedLabels;
        }
        return parsedOptions.map((option) => option.label).filter(Boolean);
    }, [hydratedLabels, parsedOptions]);

    if (!labels.length) {
        return (
            <div className="w5-cell w5-multi-select-read-cell w5-multi-select-read-cell--empty" />
        );
    }

    return (
        <div className="w5-cell w5-multi-select-read-cell">
            <MultiSelectSummaryChip fieldLabel={fieldLabel} labels={labels} />
        </div>
    );
});

MultiSelectReadCell.displayName = 'MultiSelectReadCell';

export default MultiSelectReadCell;
